import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  requestFcmToken,
  registerFcmTokenWithBackend,
} from "@/lib/firebase/fcm";

const DISMISSED_KEY = "a2z_admin_notification_prompt_dismissed_at";

function isDismissed(): boolean {
  try {
    // Session-only: every fresh app open asks again until enabled.
    return sessionStorage.getItem(DISMISSED_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Fetch + register the FCM token AFTER the banner has already closed. Run in the
 * background because `getToken()` has no timeout and can hang (service worker /
 * push endpoint) — awaiting it in the click handler previously left the Enable
 * button stuck on a loading state with the banner open. Failures are non-fatal:
 * FcmBootstrap re-attempts registration on the next app load.
 */
async function registerFcmInBackground(): Promise<void> {
  try {
    const token = await requestFcmToken();
    if (token) {
      await registerFcmTokenWithBackend(token);
    }
  } catch {
    // Non-fatal — the user already granted permission; retried on next load.
  }
}

export function NotificationPromptBanner() {
  const t = useTranslations();
  const [dismissed, setDismissed] = useState(isDismissed);
  const [requesting, setRequesting] = useState(false);

  if (dismissed) return null;
  if (typeof Notification === "undefined") return null;
  if (Notification.permission === "granted") return null;
  if (Notification.permission === "denied") return null;

  const handleEnable = async () => {
    setRequesting(true);
    let permission: NotificationPermission = "default";
    try {
      permission = await Notification.requestPermission();
    } catch {
      // Some browsers reject rather than returning "denied"; treat as no grant.
    }
    // Close the banner as soon as the user decides — never wait on the FCM
    // token round-trip (getToken can hang and would leave this stuck open).
    setRequesting(false);
    dismiss();
    if (permission === "granted") {
      void registerFcmInBackground();
    }
  };

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch { /* private browsing */ }
    setDismissed(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={dismiss}
    >
    <div
      className="flex w-[340px] max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-xl border border-[color:var(--primary-200)] bg-[color:var(--primary-50)] p-4 shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--primary-100)] text-[color:var(--primary-700)]">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </span>
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-[color:var(--primary-700)]">
            {t(keys.admin.notificationsPromptTitle)}
          </p>
          <p className="text-xs text-[color:var(--primary-600)]">
            {t(keys.admin.notificationsPromptBody)}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-end gap-2">
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg px-3 py-1.5 text-xs font-medium max-lg:min-h-10 text-[color:var(--text-muted)] hover:bg-[color:var(--surface-alt)]"
        >
          {t(keys.admin.notificationsPromptLater)}
        </button>
        <button
          type="button"
          onClick={() => void handleEnable()}
          disabled={requesting}
          className="rounded-lg bg-[color:var(--primary-500)] px-3 py-1.5 text-xs font-semibold max-lg:min-h-10 text-white hover:bg-[color:var(--primary-600)] disabled:opacity-60"
        >
          {t(keys.admin.notificationsPromptEnable)}
        </button>
      </div>
    </div>
    </div>
  );
}
